import { useCallback, type ChangeEvent, type Dispatch, type SelectHTMLAttributes, type SetStateAction } from 'react'
import { InputFrame, type InputFrameProps } from '../layout/InputFrame'
import { Icon } from '../graphics/Icon'
import { cn } from '@/utilities/misc'
import './SelectBox.css'

export type SelectBoxOption<T> = {
  label: string
  value: T
  disabled?: boolean
  hidden?: boolean
} | '-----'  // A divider

export type SelectBoxProps<T> =
  Omit<InputFrameProps, 'height' | 'children'> &
  SelectHTMLAttributes<HTMLSelectElement> &
  {
    emptyOptionLabel?: string
    options?: SelectBoxOption<T>[]
    valueToString?: (value: T) => string
    valueFromString?: (value: string) => T
    setValue?: Dispatch<SetStateAction<T>>
    frameClassName?: string
  }

export function SelectBox<T>({
  label,
  prefix,
  suffix,
  tooltip,
  tooltipPlacement,
  emptyOptionLabel,
  options,
  valueToString,
  valueFromString,
  required,
  disabled,
  width,
  className,
  frameClassName,
  onChange,
  setValue,
  children,
  ...rest
}: SelectBoxProps<T>) {

  const vts = valueToString ?? ((value: T) => value?.toString() ?? '')
  const vfs = valueFromString ?? ((value: string) => value as T)

  className = cn(
    'SelectBox',
    className,
  )

  const handleChange = useCallback((ev: ChangeEvent<HTMLSelectElement>) => {
    if (onChange)
      onChange(ev)
    if (setValue)
      setValue(vfs(ev.target.value))
  }, [setValue, onChange])

  return (
    <InputFrame
      label={label}
      prefix={prefix}
      suffix={suffix}
      tooltip={tooltip}
      tooltipPlacement={tooltipPlacement}
      required={required}
      disabled={disabled}
      width={width}
      className={cn('SelectBox', frameClassName)}
    >
      <span className='SelectWrap'>
        <select
          className={className}
          disabled={disabled}
          onChange={handleChange}
          {...rest}
        >
          {typeof emptyOptionLabel === 'string' && (
            <option value=''>{emptyOptionLabel}</option>
          )}
          {options?.map((option, index) => {
            if (option === '-----') {
              return (
                <option
                  key={`divider-${index}`}
                  value=''
                  disabled
                >
                  {'──────────'}
                </option>
              )
            }
            return (
              <option
                key={index}
                value={vts(option.value)}
                disabled={option.disabled}
                hidden={option.hidden}
              >
                {option.label}
              </option>
            )
          })}
          {children}
        </select>
        <Icon icon='chevronDown' aria-hidden />
      </span>
    </InputFrame>
  )
}
